/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Table,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { IInbound, IInboundItem } from '@/interfaces/inbound';
import { PlusOutlined, DeleteFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/th_TH';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addInbound, getInbound, updateInbound } from '@/adapters/inbound';
import { formatNumber } from '@/utils/dataUtils';
import { round } from 'lodash';
import { EInboundStatus, EInboundType } from '@/enums/EInbound';
import ProductSelector from '@/components/selector/product-selector';
import { IProduct } from '@/interfaces/product';

const InboundMaintain = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [mainForm] = Form.useForm<Partial<IInbound>>();
  const [itemForm] = Form.useForm();
  const [items, setItems] = useState<Partial<IInboundItem>[]>([]);
  const [editingKey, setEditingKey] = useState<string>('');

  const isEditing = (record: Partial<IInboundItem>) =>
    record.key === editingKey;

  const { data: inboundData, isLoading } = useQuery({
    queryKey: ['getInbound', id],
    queryFn: () => getInbound(Number(id)),
    enabled: isEdit,
  });

  useEffect(() => {
    if (inboundData?.data) {
      const inbound = inboundData.data;
      mainForm.setFieldsValue({
        ...inbound,
        invDate: inbound.invDate ? dayjs(inbound.invDate) : undefined,
        arrivalDate: inbound.arrivalDate
          ? dayjs(inbound.arrivalDate)
          : undefined,
        clearDate: inbound.clearDate ? dayjs(inbound.clearDate) : undefined,
        whReceivedDate: inbound.whReceivedDate
          ? dayjs(inbound.whReceivedDate as string)
          : undefined,
      } as any);
      setItems(
        (inbound.inboundItems || []).map(item => ({
          ...item,
          key: item.key || uuidv4(),
        })),
      );
    }
  }, [inboundData, mainForm]);

  const editItem = (record: Partial<IInboundItem>) => {
    itemForm.setFieldsValue({
      ...record,
      expiredDate: record.expiredDate
        ? dayjs(record.expiredDate as string)
        : undefined,
      invDate: record.invDate ? dayjs(record.invDate as string) : undefined,
      whReceivedDate: record.whReceivedDate
        ? dayjs(record.whReceivedDate as string)
        : undefined,
      arrivalDate: record.arrivalDate
        ? dayjs(record.arrivalDate as string)
        : undefined,
    });
    setEditingKey(record.key || '');
  };

  const cancelEdit = () => setEditingKey('');

  const saveItem = async (key: string) => {
    try {
      const row = await itemForm.validateFields();
      const newItems = [...items];
      const index = newItems.findIndex(item => item.key === key);
      if (index > -1) {
        newItems.splice(index, 1, {
          ...newItems[index],
          ...row,
          expiredDate: row.expiredDate
            ? dayjs(row.expiredDate).toISOString()
            : undefined,
          invDate: row.invDate ? dayjs(row.invDate).toISOString() : undefined,
          whReceivedDate: row.whReceivedDate
            ? dayjs(row.whReceivedDate).toISOString()
            : undefined,
          arrivalDate: row.arrivalDate
            ? dayjs(row.arrivalDate).toISOString()
            : undefined,
        });
        setItems(newItems);
        setEditingKey('');
      }
    } catch (err) {
      console.log('Validate Failed:', err);
    }
  };

  const onAddItem = () => {
    const newItem: Partial<IInboundItem> = {
      key: uuidv4(),
      id: 0,
      inboundId: Number(id) || 0,
      itemNO: items.length + 1,
      decItemNO: 0,
      productCode: '',
      pdtDescriptionE: '',
      pdtDescriptionT: '',
      totalUnit: 0,
      totalReceivedUnit: 0,
      uom: '',
      packageSize: 0,
      totalQty: 0,
      totalReceivedQty: 0,
      isSendInbound: false,
      status: EInboundStatus.OPEN,
      errorMessage: '',
      createById: '',
      createByName: '',
      createDate: '',
    };
    setItems(prev => [...prev, newItem]);
    editItem(newItem);
  };

  const onDeleteItem = (key: string) => {
    setItems(prev => prev.filter(item => item.key !== key));
  };

  const onSelectProduct = (product?: IProduct) => {
    if (!product) {
      itemForm.setFieldsValue({
        productCode: undefined,
        productId: undefined,
        pdtDescriptionE: undefined,
        uom: undefined,
      });
      setItems(prev =>
        prev.map(item =>
          item.key === editingKey
            ? {
                ...item,
                productCode: '',
                productId: undefined,
                pdtDescriptionE: '',
                uom: '',
              }
            : item,
        ),
      );
      return;
    }
    itemForm.setFieldsValue({
      productCode: product.code,
      productId: product.id,
      pdtDescriptionE: product.name,
      uom: product.baseUnitName,
    });
    setItems(prev =>
      prev.map(item =>
        item.key === editingKey
          ? {
              ...item,
              productCode: product.code,
              productId: product.id,
              pdtDescriptionE: product.name,
              uom: product.baseUnitName,
            }
          : item,
      ),
    );
  };

  interface EditableCellProps extends React.HTMLAttributes<any> {
    editing: boolean;
    dataIndex: string;
    inputType: 'number' | 'text' | 'date';
    index: number;
  }

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    editing,
    dataIndex,
    inputType,
    children,
    ...restProps
  }) => {
    let inputNode: React.ReactNode;
    if (inputType === 'number') {
      inputNode = (
        <InputNumber
          className="w-full"
          controls={false}
          decimalSeparator="."
          precision={2}
          style={{ textAlignLast: 'right' }}
        />
      );
    } else if (inputType === 'date') {
      inputNode = (
        <DatePicker className="w-full" locale={locale} format="DD/MM/YYYY" />
      );
    } else {
      inputNode = <Input />;
    }

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item name={dataIndex} style={{ margin: 0 }}>
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'itemNO',
      width: 60,
      align: 'center' as const,
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 120,
      render: (v: string) => v || '-',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      editable: true,
      width: 150,
      render: (v: string) => v || '-',
    },
    {
      title: 'สินค้า',
      dataIndex: 'pdtDescriptionE',
      render: (v: string, rec: Partial<IInboundItem>) =>
        isEditing(rec) ? (
          <ProductSelector
            value={rec.productId}
            handleSelectData={onSelectProduct}
            size="middle"
          />
        ) : (
          v || '-'
        ),
    },
    {
      title: 'จำนวน',
      dataIndex: 'totalUnit',
      width: 120,
      align: 'right' as const,
      editable: true,
      inputType: 'number',
      render: (v: number) => formatNumber(v, 2),
    },
    {
      title: 'หน่วย',
      dataIndex: 'uom',
      width: 80,
      editable: true,
      inputType: 'text',
    },
    {
      title: 'วันหมดอายุ',
      dataIndex: 'expiredDate',
      width: 130,
      editable: true,
      inputType: 'date',
      render: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY') : '-'),
    },
    {
      title: '',
      width: 110,
      dataIndex: 'action',
      align: 'center' as const,
      render: (_: any, rec: Partial<IInboundItem>) => {
        const editing = isEditing(rec);
        return editing ? (
          <span className="flex gap-1">
            <Button
              type="link"
              size="small"
              onClick={() => saveItem(rec.key || '')}
            >
              บันทึก
            </Button>
            <Button type="link" size="small" danger onClick={cancelEdit}>
              ยกเลิก
            </Button>
          </span>
        ) : (
          <span className="flex gap-1 justify-center">
            <Button
              type="link"
              size="small"
              disabled={editingKey !== ''}
              onClick={() => editItem(rec)}
            >
              แก้ไข
            </Button>

            <DeleteFilled
              className="text-red-600 cursor-pointer"
              onClick={() => onDeleteItem(rec.key || '')}
            />
          </span>
        );
      },
    },
  ];

  const mergedColumns: any = columns.map(col => {
    if (!(col as any).editable) return col;
    return {
      ...col,
      onCell: (record: Partial<IInboundItem>) => ({
        inputType: (col as any).inputType || 'text',
        dataIndex: col.dataIndex,
        editing: isEditing(record),
      }),
    };
  });

  const mutation = useMutation({
    mutationFn: async (values: Partial<IInbound>) => {
      if (isEdit) return await updateInbound({ ...values, id: Number(id) });
      return await addInbound(values);
    },
    onSuccess: res => {
      if (res?.status !== 200 && res?.status !== 201) throw new Error();
      notification.success({
        message: isEdit ? 'บันทึกสำเร็จ' : 'สร้างสำเร็จ',
      });
      navigate('/inbound');
    },
    onError: err => {
      console.log(err);
      notification.error({ message: 'เกิดข้อผิดพลาด' });
    },
  });

  const handleSave = () => {
    mainForm
      .validateFields()
      .then((values: Partial<IInbound>) => {
        const payload: Partial<IInbound> = {
          ...values,
          invDate: values.invDate
            ? dayjs(values.invDate as any).toISOString()
            : undefined,
          arrivalDate: values.arrivalDate
            ? dayjs(values.arrivalDate as any).toISOString()
            : undefined,
          clearDate: values.clearDate
            ? dayjs(values.clearDate as any).toISOString()
            : undefined,
          whReceivedDate: values.whReceivedDate
            ? dayjs(values.whReceivedDate as any).toISOString()
            : undefined,
          inboundItems: items as IInboundItem[],
        };
        mutation.mutate(payload);
      })
      .catch(info => console.log('Validate Failed:', info));
  };

  console.log(items);

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate('/inbound')}
              >
                นำเข้าสินค้า
              </div>
            ),
          },
          { title: isEdit ? 'แก้ไขใบนำเข้า' : 'สร้างใบนำเข้าใหม่' },
        ]}
      />

      <div className="flex w-full mt-5">
        <Form
          form={mainForm}
          layout="vertical"
          className="w-full"
          autoComplete="off"
        >
          <Row gutter={12}>
            {/* <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="เลขที่เอกสาร" name="docNo">
                <Input
                  size="large"
                  placeholder="เลขที่เอกสาร"
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="เลขที่ใบขนสินค้า (DEC)" name="decNO">
                <Input size="large" placeholder="เลขที่ใบขนสินค้า" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="ECS Job No." name="ecS_JobNO">
                <Input size="large" placeholder="ECS Job No." />
              </Form.Item>
            </Col> */}
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound>
                label="เลขที่ Invoice (INV)"
                name="invNO"
                rules={[{ required: true }]}
              >
                <Input size="large" placeholder="เลขที่ Invoice" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound>
                label="วันที่ Invoice"
                name="invDate"
                rules={[{ required: true }]}
              >
                <DatePicker
                  className="w-full"
                  size="large"
                  locale={locale}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="วันที่สินค้าถึง" name="arrivalDate">
                <DatePicker
                  className="w-full"
                  size="large"
                  locale={locale}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound>
                label="วันที่รับเข้าคลัง"
                name="whReceivedDate"
              >
                <DatePicker
                  className="w-full"
                  size="large"
                  locale={locale}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            {/* <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="Master B/L" name="masterBL">
                <Input size="large" placeholder="Master B/L" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="House B/L" name="houseBL">
                <Input size="large" placeholder="House B/L" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="น้ำหนักรวม" name="totalGrossW">
                <InputNumber
                  size="large"
                  className="w-full"
                  controls={false}
                  precision={2}
                  placeholder="น้ำหนักรวม"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="หน่วยน้ำหนัก" name="weightUnit">
                <Input size="large" placeholder="หน่วยน้ำหนัก" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="ประเทศต้นทาง" name="consCountry">
                <Input size="large" placeholder="ประเทศต้นทาง" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="ชื่อผู้ส่งออก" name="consName">
                <Input size="large" placeholder="ชื่อผู้ส่งออก" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound> label="รหัสผู้ส่งออก" name="consCode">
                <Input size="large" placeholder="รหัสผู้ส่งออก" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<IInbound>
                label="ประเภทการนำเข้า"
                name="type"
                initialValue={EInboundType.NORMAL}
              >
                <Select
                  size="large"
                  options={[
                    { value: EInboundType.NORMAL, label: 'นำเข้าปกติ' },
                    { value: EInboundType.RETURN, label: 'ส่งคืน' },
                  ]}
                />
              </Form.Item>
            </Col> */}
          </Row>
        </Form>
      </div>

      <div className="flex w-full mt-5">
        <Form form={itemForm} component={false} className="w-full">
          <Table
            components={{ body: { cell: EditableCell } }}
            locale={{ emptyText: 'ไม่มีรายการ' }}
            size="middle"
            bordered
            dataSource={items}
            columns={mergedColumns}
            rowKey="key"
            className="w-full"
            rowClassName="editable-row"
            pagination={false}
            loading={isLoading}
            scroll={{ x: 1200 }}
          />
        </Form>
      </div>

      <div className="flex justify-start mt-3">
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={onAddItem}
          disabled={editingKey !== ''}
        >
          เพิ่มรายการ
        </Button>
      </div>
      <div className="flex justify-end mt-5 gap-2">
        <Button size="large" onClick={() => navigate('/inbound')}>
          ยกเลิก
        </Button>
        <Button
          type="primary"
          size="large"
          className="font-bold min-w-[150px]"
          loading={mutation.isPending}
          disabled={!!editingKey}
          onClick={handleSave}
        >
          {isEdit ? 'บันทึกการแก้ไข' : 'สร้างใบนำเข้า'}
        </Button>
      </div>
    </Card>
  );
};

export default InboundMaintain;
